"use client";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import Link from "next/link";
import { usePathname } from "next/navigation";

import { Typography } from "@/components/shared/Typography";
import CustomImage from "../../shared/CustomImage";
import { getNewsDetailsUrl } from "@/utils/getNewsDetailsUrl";
import { ArticleListType } from "@/types/article";
import { getImageUrl } from "@/utils/getImageUrl";
import { getLocaleFromPathname } from "@/utils/getLocaleFromPath";
import TitleTag from "../../shared/TitleTag";

import "swiper/css";
import "swiper/css/autoplay";

export type SidebarNewsGridCarousalProps = {
  articles: ArticleListType[];
};

export default function SidebarNewsGridCarousal({ articles }: SidebarNewsGridCarousalProps) {
  const pathname = usePathname();
  const locale = getLocaleFromPathname(pathname);

  return (
    <div>
      <Swiper
        modules={[Autoplay]}
        loop={articles?.length > 1}
        speed={500}
        slidesPerView={1}
        spaceBetween={16}
        autoplay={{ delay: 5000, disableOnInteraction: false }}
      >
        {articles?.map(
          (
            { title, slug, image, categoryId, subCategoryId, focusPosition, isLiveNews, isInPrint, publishedAt },
            index,
          ) => {
            return (
              <SwiperSlide key={index}>
                <article className="w-full group flex items-start gap-4">
                <Link
                  href={getNewsDetailsUrl(categoryId, subCategoryId, slug, locale)}
                  aria-label={`View news: ${title}`}
                >
                  <div className="w-[80px] min-w-[80px] max-w-[80px] overflow-hidden rounded-lg aspect-[16/14]">
                    <CustomImage
                      src={getImageUrl(image?.sizes?.sd?.filename || image?.filename)}
                      alt={image?.alt || ""}
                      className={`object-cover object-${focusPosition} w-full h-full img-zoom-hover`}
                      phImg="/images/placeholder-grey-sd.png"
                      loading="eager"
                    />
                  </div>
                </Link>

                <Link
                  href={getNewsDetailsUrl(categoryId, subCategoryId, slug, locale)}
                  aria-label={`View news: ${title}`}
                >
                  <Typography
                    variant="h6"
                    className="line-clamp-3 transition-all hover:text-primaryColor"
                  >
                    {isLiveNews &&
                      publishedAt &&
                      Date.now() - new Date(publishedAt).getTime() <= 12 * 60 * 60 * 1000 && (
                        <TitleTag labelKey="LIVE_NEWS" />
                      )}
                    {isInPrint && <TitleTag labelKey="PRINT_EDITION" />}
                    {title}
                  </Typography>
                </Link>
              </article>
            </SwiperSlide>
            );
          },
        )}
      </Swiper>
    </div>
  );
}
